'use client';

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import type { RootState } from '../../app/store';
import BarchartComp from '../../components/barChart/BarChart';
import Button from '../../components/button/Button';
import { EarningInfoBox } from '../../components/earningInfoBox/EarningInfoBox';
import HistoryRouting from '../../components/HistoryRouting';
import type { FilterByDateWithFlag } from '../../components/lineChart/LineChart';
import Spinner from '../../components/spinner/Spinner';
import Table from '../../components/table/Table';
import {
  GetDashboardEarningInfo,
  GetDashboardInfo,
  GetGraphPartnerPayouts,
} from '../../features/partnerSlice';
import {
  GetDonationReports,
  GetGraphCampaignData,
  GetGraphDonationTotals,
  GetGraphMemberEarnings,
  GetGraphPartnerEarnings,
  setDonationReports,
  setGraphCampaignData,
  setGraphDonationTotals,
  setGraphMemberEarnings,
  setGraphPartnerEarnings,
} from '../../features/reportingSlice';
import { useEndpointLoading } from '../../hooks/useApiLoading';
import { api } from '../../utils/api/apiClient';
import { useLocation } from 'react-router';
import { formatDateString } from '../../assets/helper';

type DonationReportRow = {
  name: string;
  total: number | string;
  donors: number | string;
  currency?: string;
};

const donationReportsConfig = {
  headers: [
    { key: 'month', label: 'Month' },
    { key: 'total', label: 'Total' },
    { key: 'donors', label: 'Donors' },
  ],
};

const formatDonationAmount = (
  value: number | string | undefined | null,
  currency = 'USD'
): string => {
  if (value === undefined || value === null || value === '') return '$0.00';
  if (typeof value === 'string' && value.trim().startsWith('$')) return value;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return String(value);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(numeric);
  } catch {
    return `$${numeric.toFixed(2)}`;
  }
};

const PartnerErnings = () => {
  const location = useLocation();
  const incomingTab = location.state?.activeTab || '1';
  const [activeTab, setActiveTab] = useState<'1' | '2' | '3' | '4'>(
    incomingTab
  );
  const isEarningsLoading = useEndpointLoading('/usr/p/c/d', 'POST');
  const isDonationSummaryLoading = useEndpointLoading('/usr/p/d/d', 'POST');
  const [customDate, setCustomDate] = useState<FilterByDateWithFlag>({
    startDate: '',
    endDate: '',
    typeName: '',
  });
  const {
    dashboardInfo,
    GraphPartnerPayouts,
    dashboardEarningInfo,
    loadingStates: PartnerLoadingState,
  } = useSelector((state: RootState) => state.partner);
  const {
    graphPartnerEarnings,
    graphMemberEarnings,
    campaignData,
    graphDonationTotals,
    donationReports,
    loadingStates,
  } = useSelector((state: RootState) => state.reporting);
  const dispatch = useAppDispatch();
  const [partnerData, setPartnerData] = useState<any>(null);
  const [donationData, setDonationData] = useState<any>(null);

  useEffect(() => {
    if (!dashboardInfo) {
      dispatch(GetDashboardInfo());
    }
  }, [dashboardInfo, dispatch]);
  useEffect(() => {
    if (!dashboardEarningInfo) {
      dispatch(GetDashboardEarningInfo());
    }
  }, [dashboardEarningInfo, dispatch]);

  useEffect(() => {
    const { typeName, ...date } = customDate;
    if (activeTab !== '3') {
      if (typeName === 'PartnerEarnings') {
        dispatch(GetGraphPartnerEarnings(date));
      } else if (!graphPartnerEarnings && activeTab === '1') {
        dispatch(GetGraphPartnerEarnings());
      }

      if (typeName === 'PartnerPayouts') {
        dispatch(GetGraphPartnerPayouts(date));
      } else if (!GraphPartnerPayouts && activeTab === '1') {
        dispatch(GetGraphPartnerPayouts());
      }
      if (typeName === 'MemberEarnings') {
        dispatch(GetGraphMemberEarnings(date));
      } else if (!graphMemberEarnings && activeTab === '2') {
        dispatch(GetGraphMemberEarnings());
      }
      if (typeName === 'DonationTotals') {
        dispatch(GetGraphDonationTotals(date));
        dispatch(GetDonationReports(date));
      } else if (activeTab === '4') {
        if (!graphDonationTotals) {
          dispatch(GetGraphDonationTotals());
        }
        if (!donationReports) {
          dispatch(GetDonationReports());
        }
      }
    }
    if (activeTab === '3' && !campaignData) {
      dispatch(GetGraphCampaignData());
    }

    if (typeName) {
      setCustomDate(prev => ({ ...prev, typeName: '' }));
    }
  }, [
    activeTab,
    customDate,
    dispatch,
    graphPartnerEarnings,
    graphMemberEarnings,
    GraphPartnerPayouts,
    campaignData,
    graphDonationTotals,
    donationReports,
  ]);

  useEffect(() => {
    return () => {
      dispatch(setGraphMemberEarnings(null));
      dispatch(setGraphPartnerEarnings(null));
      dispatch(setGraphCampaignData(null));
      dispatch(setGraphDonationTotals(null));
      dispatch(setDonationReports(null));
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.post('/usr/p/c/d');
        setPartnerData(response);
      } catch (error) {
        console.error('API Error:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab !== '4') return;

    const fetchDonationSummary = async () => {
      try {
        const response = await api.post('/usr/p/d/d');
        setDonationData(response);
      } catch (error) {
        console.error('Donation summary API Error:', error);
      }
    };

    fetchDonationSummary();
  }, [activeTab]);

  // Determine which boxes to show based on the conditions
  const shouldShowBoxB = partnerData?.receivePayout === '';
  const shouldShowBoxC = !shouldShowBoxB;
  const donationReportRows: DonationReportRow[] = Array.isArray(donationReports)
    ? donationReports
    : [];
  const donationTableData = donationReportRows.map(row => ({
    month: row.name,
    total: formatDonationAmount(row.total, row.currency),
    donors: Number(row.donors) || 0,
  }));
  const isSummaryLoading =
    activeTab === '1'
      ? isEarningsLoading
      : activeTab === '4'
        ? isDonationSummaryLoading
        : false;

  return (
    <div className='mb-8'>
      {/* Toggle Button */}
      <HistoryRouting history='Reporting/Earnings Data' />
      <div className='space-y-10'>
        {/* A - B - C - D Boxes */}
        {isSummaryLoading ? (
          <div className='ml-12 my-4'>
            <Spinner />
          </div>
        ) : activeTab === '1' ? (
          <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3'>
            {/* A - Life time program payouts (always shown) */}
            <EarningInfoBox
              title='Lifetime Partner Payouts'
              value={dashboardEarningInfo?.partnerPayouts?.value || '$0.00'}
            />

            {shouldShowBoxB && (
              <EarningInfoBox
                title='Next Payout'
                value={partnerData?.available || '$0.00'}
                // badge='NEW'
                extraContent={
                  <p className='text-sm text-gray-600'>
                    Date: {formatDateString(partnerData?.payoutDate) ?? 'N/A'}
                  </p>
                }
              />
            )}

            {shouldShowBoxC && (
              <EarningInfoBox
                title='Confirmed Partner Earnings'
                value={
                  dashboardEarningInfo?.partnerConfirmedEarnings?.value ||
                  '$0.00'
                }
                tooltip='You need to reach $25 to qualify for payouts'
              />
            )}

            <EarningInfoBox
              title='Pending Partner Earnings'
              value={
                dashboardEarningInfo?.partnerPendingEarnings?.value || '$0.00'
              }
              tooltip='Earnings that are still being processed'
            />
          </div>
        ) : activeTab === '4' ? (
          <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3'>
            <EarningInfoBox
              title='Lifetime Donations'
              value={formatDonationAmount(donationData?.totalEarned)}
            />
            <EarningInfoBox
              title='Next Donation'
              value={formatDonationAmount(donationData?.available)}
              extraContent={
                <p className='text-sm text-gray-600'>
                  Date: {formatDateString(donationData?.payoutDate) ?? 'N/A'}
                </p>
              }
            />
            <EarningInfoBox
              title='Pending Donations'
              value={formatDonationAmount(donationData?.pending)}
            />
          </div>
        ) : (
          <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3'>
            <EarningInfoBox
              title='Lifetime Member Payouts'
              value={dashboardEarningInfo?.userPayouts?.value || '$0.00'}
            />
            <EarningInfoBox
              title='Confirmed Member Cashback'
              value={
                dashboardEarningInfo?.usersConfirmedEarnings?.value || '$0.00'
              }
            />
            <EarningInfoBox
              title='Pending Member Cashback'
              value={
                dashboardEarningInfo?.usersPendingEarnings?.value || '$0.00'
              }
            />
          </div>
        )}
        <div className='border border-gray-200 primary-shadow px-7 pt-7 pb-5'>
          <div className='flex gap-10'>
            <Button
              className={` ${
                activeTab === '1'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('1')}
            >
              Partner Cashback
            </Button>
            <Button
              className={` ${
                activeTab === '2'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('2')}
            >
              Member Cashback
            </Button>
            <Button
              className={` ${
                activeTab === '4'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('4')}
            >
              Donations
            </Button>
           {/* <Button
              className={` ${
                activeTab === '3'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('3')}
            >
              Program Booster Earnings
            </Button>*/}
          </div>
          {/* Display Charts or List View */}
          <div className='grid grid-cols-1 '>
            {activeTab === '1' && (
              <div className='space-y-6 my-6'>
                <BarchartComp
                  data={graphPartnerEarnings}
                  title='Partner Earnings'
                  dataKey1='pending'
                  name1='pending'
                  dataKey2='confirmed'
                  name2='confirmed'
                  dataKey3='rejected'
                  name3='rejected/cancelled/refunded'
                  chartName='PartnerEarnings'
                  setCustomDate={setCustomDate}
                  dollar={true}
                  isFiltering={true}
                  description='Monthly Partner Earnings'
                  isLoading={loadingStates['GetGraphPartnerEarnings']}
                  showExport
                  isShadowBox
                  isStack
                />
                <BarchartComp
                  title='Partner Payouts'
                  data={GraphPartnerPayouts}
                  dataKey1='amount'
                  name1='Partner Payouts'
                  chartName='PartnerPayouts'
                  dollar={true}
                  isFiltering={true}
                  isOneColumn={true}
                  description='Monthly Payouts'
                  setCustomDate={setCustomDate}
                  isLoading={PartnerLoadingState['GetGraphPartnerPayouts']}
                  showExport
                  isShadowBox
                />
              </div>
            )}
            {activeTab === '2' && (
              <BarchartComp
                data={graphMemberEarnings}
                dataKey1='pending'
                name1='pending'
                dataKey2='confirmed'
                name2='confirmed'
                dataKey3='rejected'
                name3='rejected'
                chartName='MemberEarnings'
                setCustomDate={setCustomDate}
                dollar={true}
                isFiltering={true}
                description='Monthly Active Members Earnings'
                isLoading={loadingStates['GetGraphMemberEarnings']}
                showExport
                isStack
              />
            )}
            {activeTab === '3' && (
              <BarchartComp
                data={campaignData}
                dataKey1='qualifiedUsers'
                name1='Qualified Sign-Ups'
                dataKey2='notQualifiedUsers'
                name2='Unqualified Sign-ups'
                chartName='MemberEarnings'
                setCustomDate={setCustomDate}
                description='Campaign Rewards'
                isLoading={loadingStates['GetGraphCampaignData']}
                showExport
                isStack
              />
            )}
            {activeTab === '4' && (
              <div className='space-y-6 my-6'>
                <BarchartComp
                  title='Donation Totals by Month'
                  data={graphDonationTotals}
                  dataKey1='amount'
                  name1='Donations'
                  chartName='DonationTotals'
                  dollar={true}
                  isFiltering={true}
                  isOneColumn={true}
                  barColor1='#00C389'
                  description='Donation Totals by Month'
                  setCustomDate={setCustomDate}
                  isLoading={loadingStates['GetGraphDonationTotals']}
                  showExport
                  isShadowBox
                />

                {loadingStates['GetDonationReports'] ? (
                  <div className='py-6'>
                    <Spinner />
                  </div>
                ) : donationTableData.length > 0 ? (
                  <Table
                    data={donationTableData}
                    config={donationReportsConfig}
                    showExportButton={true}
                    width='w-full'
                    enableSorting={false}
                    showSearch={false}
                    title='Monthly Donation Reports'
                  />
                ) : (
                  <p className='italic text-gray-600 text-center py-6'>
                    No data available
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerErnings;
